import { useState, useRef, useCallback } from "react";
import ReactCrop, {
  type Crop,
  type PixelCrop,
  type PercentCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { type AreaCoordinates } from "../api/monitors";

interface AreaSelectorProps {
  imageSrc: string;
  onCropChange: (areaCoordinates: AreaCoordinates) => void;
  onCropComplete?: (areaCoordinates: AreaCoordinates) => void;
  className?: string;
  initialCropMode?: 'fullpage' | 'selection';
}

const calculateCropForMode = (
  mode: 'fullpage' | 'selection',
  naturalWidth: number,
  naturalHeight: number,
  clientWidth: number,
  clientHeight: number,
  containerRef: React.RefObject<HTMLDivElement>
): PercentCrop => {
    if (mode === 'fullpage') {
      return {
        unit: "%",
        x: 0,
        y: 0,
        width: 100,
        height: 100,
      };
    }

    const targetWidthDisplay = 320;
    const targetHeightDisplay = 220;

    const scaleX = naturalWidth / clientWidth;
    const scaleY = naturalHeight / clientHeight;
    const targetWidth = targetWidthDisplay * scaleX;
    const targetHeight = targetHeightDisplay * scaleY;

    const container = containerRef.current;
    const viewportHeight = container ? container.clientHeight : clientHeight;
    const scrollTop = container ? container.scrollTop : 0;
    const visibleTopNatural = scrollTop * scaleY;
    const visibleHeightNatural = viewportHeight * scaleY;

    const pixelX = (naturalWidth - targetWidth) / 2;
    const pixelY = visibleTopNatural + (visibleHeightNatural - targetHeight) / 2;

    const percentX = (pixelX / naturalWidth) * 100;
    const percentY = (pixelY / naturalHeight) * 100;
    const percentWidth = (targetWidth / naturalWidth) * 100;
    const percentHeight = (targetHeight / naturalHeight) * 100;

    return {
      unit: "%",
      x: Math.min(100, Math.max(0, percentX)),
      y: Math.min(100, Math.max(0, percentY)),
      width: Math.min(100, percentWidth),
      height: Math.min(100, percentHeight),
    };
  }

const AreaSelector: React.FC<AreaSelectorProps> = ({
  imageSrc,
  onCropChange,
  onCropComplete,
  className = "",
  initialCropMode = 'selection',
}) => {
  const [cropMode, setCropMode] = useState<'fullpage' | 'selection'>(initialCropMode);
  const [crop, setCrop] = useState<Crop>({
    unit: "%",
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [dynamicMinSize, setDynamicMinSize] = useState<number>(100);
  const [showInfoBanner, setShowInfoBanner] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleCropChange = useCallback(
    (pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
      if (pixelCrop.width === 0 && pixelCrop.height === 0 && imgRef.current) {
        const image = imgRef.current;
        const { naturalWidth, naturalHeight } = image;
        const MIN_DIMENSION = dynamicMinSize;

        // Use percentage coordinates to calculate actual pixel position
        const clickPixelX = (percentCrop.x / 100) * naturalWidth;
        const clickPixelY = (percentCrop.y / 100) * naturalHeight;

        // Center the minimum crop around the click point
        let newX = clickPixelX - MIN_DIMENSION / 2;
        let newY = clickPixelY - MIN_DIMENSION / 2;

        // Clamp coordinates to image boundaries
        if (newX < 0) newX = 0;
        if (newY < 0) newY = 0;
        if (newX + MIN_DIMENSION > naturalWidth) {
          newX = naturalWidth - MIN_DIMENSION;
        }
        if (newY + MIN_DIMENSION > naturalHeight) {
          newY = naturalHeight - MIN_DIMENSION;
        }

        // Create new crop with minimum dimensions
        const newPixelCrop: PixelCrop = {
          unit: "px",
          x: newX,
          y: newY,
          width: MIN_DIMENSION,
          height: MIN_DIMENSION,
        };

        const newPercentCrop: PercentCrop = {
          unit: "%",
          x: (newX / naturalWidth) * 100,
          y: (newY / naturalHeight) * 100,
          width: (MIN_DIMENSION / naturalWidth) * 100,
          height: (MIN_DIMENSION / naturalHeight) * 100,
        };

        // Use the new crop instead of the 0x0 crop
        pixelCrop = newPixelCrop;
        percentCrop = newPercentCrop;
      }

      // CRITICAL: Update the crop state to keep the selection visible
      setCrop(percentCrop);
      setCompletedCrop(pixelCrop);

      if (!imgRef.current) return;

      const { naturalWidth, naturalHeight } = imgRef.current;

      // Calculate coordinates for API (display removed)

      // Create API-compatible AreaCoordinates object
      const areaCoordinates: AreaCoordinates = {
        viewport_width: naturalWidth,
        viewport_height: naturalHeight,
        percent_x: Math.round(percentCrop.x * 100) / 100,
        percent_y: Math.round(percentCrop.y * 100) / 100,
        percent_width: Math.round(percentCrop.width * 100) / 100,
        percent_height: Math.round(percentCrop.height * 100) / 100,
      };

      onCropChange(areaCoordinates);
    },
    [onCropChange, dynamicMinSize]
  );

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth, naturalHeight, clientWidth, clientHeight } =
        e.currentTarget;

      // Calculate dynamic minimum size for 100px on screen
      const scaleX = naturalWidth / clientWidth;
      const scaleY = naturalHeight / clientHeight;
      const scale = Math.max(scaleX, scaleY);
      const calculatedMinSize = Math.round(100 * scale);

      setDynamicMinSize(calculatedMinSize);

      const initialCrop = calculateCropForMode(
        cropMode,
        naturalWidth,
        naturalHeight,
        clientWidth,
        clientHeight,
        containerRef
      );

      setCrop(initialCrop);

      // Force initial crop calculation
      const pixelCrop: PixelCrop = {
        unit: "px",
        x: (initialCrop.x / 100) * naturalWidth,
        y: (initialCrop.y / 100) * naturalHeight,
        width: (initialCrop.width / 100) * naturalWidth,
        height: (initialCrop.height / 100) * naturalHeight,
      };

      handleCropChange(pixelCrop, initialCrop);
    },
    [cropMode, handleCropChange]
  );

  const handleCropComplete = useCallback(
    (pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
      setCompletedCrop(pixelCrop);

      if (!imgRef.current || !onCropComplete) return;

      const { naturalWidth, naturalHeight } = imgRef.current;

      // Create API-compatible AreaCoordinates object
      const areaCoordinates: AreaCoordinates = {
        viewport_width: naturalWidth,
        viewport_height: naturalHeight,
        percent_x: Math.round(percentCrop.x * 100) / 100,
        percent_y: Math.round(percentCrop.y * 100) / 100,
        percent_width: Math.round(percentCrop.width * 100) / 100,
        percent_height: Math.round(percentCrop.height * 100) / 100,
      };

      onCropComplete(areaCoordinates);
    },
    [onCropComplete]
  );

  // const clearCrop = useCallback(() => {
  //   // Reset to initial crop position but keep it visible
  //   const newCrop = {
  //     unit: '%' as const,
  //     x: 0,
  //     y: 0,
  //     width: 100,
  //     height: 100
  //   };
  //   setCrop(newCrop);
  //   setCompletedCrop(undefined);
  //   setCoordinates(null);
  //
  //   // Trigger coordinate calculation for the reset position if image is loaded
  //   if (imgRef.current) {
  //     const { naturalWidth, naturalHeight } = imgRef.current;
  //     const pixelCrop: PixelCrop = {
  //       unit: 'px',
  //       x: (newCrop.x / 100) * naturalWidth,
  //       y: (newCrop.y / 100) * naturalHeight,
  //       width: (newCrop.width / 100) * naturalWidth,
  //       height: (newCrop.height / 100) * naturalHeight
  //     };
  //     handleCropChange(pixelCrop, newCrop);
  //   }
  // }, [handleCropChange]);

  const toggleCropMode = useCallback(() => {
    if (!imgRef.current) return;

    const { naturalWidth, naturalHeight, clientWidth, clientHeight } = imgRef.current;
    const newMode = cropMode === 'fullpage' ? 'selection' : 'fullpage';
    setCropMode(newMode);

    const newCrop = calculateCropForMode(
      newMode,
      naturalWidth,
      naturalHeight,
      clientWidth,
      clientHeight,
      containerRef
    );

    setCrop(newCrop);

    const pixelCrop: PixelCrop = {
      unit: "px",
      x: (newCrop.x / 100) * naturalWidth,
      y: (newCrop.y / 100) * naturalHeight,
      width: (newCrop.width / 100) * naturalWidth,
      height: (newCrop.height / 100) * naturalHeight,
    };

    handleCropChange(pixelCrop, newCrop);
  }, [cropMode, handleCropChange]);

  const isValidCrop =
    completedCrop && completedCrop.width >= 50 && completedCrop.height >= 50;

  return (
    <div className={`${className}`}>
      <div
        ref={containerRef}
        className="relative max-h-[500px] overflow-y-auto overflow-x-hidden border border-gray-200 rounded-lg w-full"
      >
        <button
          onClick={toggleCropMode}
          className="sticky top-3 right-3 float-right z-20 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 flex items-center gap-2 text-sm font-medium transition-colors shadow-lg mb-[-48px] mr-3"
        >
          {cropMode === 'fullpage' ? (
            <svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.3333 13.3333H2C1.46957 13.3333 0.960859 13.1226 0.585786 12.7475C0.210714 12.3725 0 11.8638 0 11.3333V0.666667C0 0.489856 0.0702379 0.320286 0.195262 0.195262C0.320286 0.0702379 0.489856 0 0.666667 0H10C10.1768 0 10.3464 0.0702379 10.4714 0.195262C10.5964 0.320286 10.6667 0.489856 10.6667 0.666667V8.66667H13.3333V11.3333C13.3333 11.8638 13.1226 12.3725 12.7475 12.7475C12.3725 13.1226 11.8638 13.3333 11.3333 13.3333ZM10.6667 10V11.3333C10.6667 11.5101 10.7369 11.6797 10.8619 11.8047C10.987 11.9298 11.1565 12 11.3333 12C11.5101 12 11.6797 11.9298 11.8047 11.8047C11.9298 11.6797 12 11.5101 12 11.3333V10H10.6667ZM9.33333 12V1.33333H1.33333V11.3333C1.33333 11.5101 1.40357 11.6797 1.5286 11.8047C1.65362 11.9298 1.82319 12 2 12H9.33333ZM2.66667 3.33333H8V4.66667H2.66667V3.33333ZM2.66667 6H8V7.33333H2.66667V6ZM2.66667 8.66667H6V10H2.66667V8.66667Z" fill="currentColor"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 5V2h3M14 5V2h-3M2 11v3h3M14 11v3h-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          {cropMode === 'fullpage' ? 'Full page' : 'Selection'}
        </button>

        <ReactCrop
          crop={crop}
          onChange={handleCropChange}
          onComplete={handleCropComplete}
          minWidth={100}
          minHeight={100}
          className="w-full"
        >
          <img
            ref={imgRef}
            src={imageSrc}
            alt="Screenshot for area selection"
            onLoad={onImageLoad}
            className="w-full h-auto block"
          />
        </ReactCrop>

        {/* Info Banner - only show in selection mode */}
        {cropMode === 'selection' && showInfoBanner && (
          <div className="sticky bottom-3 left-3 right-3 z-20 bg-gray-100/80 border border-gray-300 p-3 py-2 mx-14 rounded-lg flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-3">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                <path d="M10 20C4.477 20 0 15.523 0 10C0 4.477 4.477 0 10 0C15.523 0 20 4.477 20 10C20 15.523 15.523 20 10 20ZM9 9V15H11V9H9ZM9 5V7H11V5H9Z" fill="#72889E"/>
              </svg>
              <span className="text-gray-700 text-sm font-medium">
                Adjust the selection to focus on the area of the page you'd like us to monitor.
              </span>
            </div>
            <button
              onClick={() => setShowInfoBanner(false)}
              className="text-gray-500 hover:text-gray-700 ml-2 flex-shrink-0"
            >
              <div className="bg-white rounded-lg p-1">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 6l8 8M14 6l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Coordinate Display */}
      {/* {coordinates && (
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h3 className="font-medium text-sm text-gray-700 mb-2">Selection Coordinates</h3>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <h4 className="font-medium text-gray-600 mb-1">Pixel Coordinates</h4>
              <div className="space-y-1">
                <div>X: {coordinates.pixelX}px</div>
                <div>Y: {coordinates.pixelY}px</div>
                <div>Width: {coordinates.pixelWidth}px</div>
                <div>Height: {coordinates.pixelHeight}px</div>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-600 mb-1">Percentage Coordinates</h4>
              <div className="space-y-1">
                <div>X: {coordinates.percentageX}%</div>
                <div>Y: {coordinates.percentageY}%</div>
                <div>Width: {coordinates.percentageWidth}%</div>
                <div>Height: {coordinates.percentageHeight}%</div>
              </div>
            </div>
          </div>
        </div>
      )} */}

      {/* Validation Message */}
      {completedCrop && !isValidCrop && (
        <div className="text-red-600 text-sm p-2 bg-red-50 rounded border border-red-200">
          Selection must be at least 50x50 pixels
        </div>
      )}
    </div>
  );
};

export default AreaSelector;
